/* eslint-disable no-console */
import moment from 'moment';
import { GameRow } from './game-row';
import { TeamGame, TeamDropInfo, TeamDropInfoType } from './team-game';
import { RemovedTeam } from './removed-team';
import { ScheduleRowEntity } from '../../../apis/schedule/schedule-row-entity';
import { OpponentsViewModel } from '../../../components/schedule/opponents-view-model';
import { SerieSettingEntity } from '../../../apis/serie/serie-setting-entity';
import { CustomIntervalEntity } from '../../../apis/custom-interval/custom-interval-entity';
import { TeamSettingEntity } from '../../../apis/team/team-setting-entity';
import { VenueEntity } from '../../../apis/venue/venue-entity';
import { ScheduleViewModel } from '../../../components/schedule/schedule-view-model';
import { Penalty } from '../../../model/penalty';
import { GroupEntity } from '../../../apis/group/group-entity';
import { GroupColorScheme } from '../../../components/schedule/group-color-scheme';

export class EditScheduleViewModel {

    public id: string = null;
    private _opponentList: OpponentsViewModel[] = [];
    public get opponentList(): OpponentsViewModel[] {
        return this._opponentList;
    }
    public set opponentList(value: OpponentsViewModel[]) {
        this._opponentList = value;
    }
    public removedTeams: RemovedTeam[] = [];
    public intervalCount: number = 0;
    public gameRows: GameRow[] = [];
    public penalty: Penalty = null;
    public venueModels: VenueModel[] = [];
    public selectedTeamGame: TeamGame | null = null;
    public selectedIsHome: boolean = false;
    public selectedRemovedTeam: RemovedTeam | null = null;
    private _scheduleRows: ScheduleRowEntity[] = [];
    private _extraGames: TeamExtraGame[] = [];
    private _serie: SerieSettingEntity = null;
    private _customIntervals: CustomIntervalEntity[] = [];
    private _teamSettings: TeamSettingEntity[] = [];
    private _groupColorScheme: GroupColorScheme = new GroupColorScheme();
    constructor(games: ScheduleRowEntity[], serie: SerieSettingEntity, customIntervals: CustomIntervalEntity[], teamSettings: TeamSettingEntity[], _venues: VenueEntity[], groups: GroupEntity[] = []) {

        this._serie = serie;
        this._customIntervals = customIntervals;
        this._teamSettings = teamSettings;
        this._scheduleRows.clear();
        this._scheduleRows.addRange(games);
        // eslint-disable-next-line prefer-spread
        this.intervalCount = Math.max.apply(Math, games.map(g => g.interval + 1));
        this.venueModels = games.select(g => g.fieldId).distinct<number>().orderBy(f => f).select(n => new VenueModel(n, null));

        this._setup();
        this._groupColorScheme = new GroupColorScheme(teamSettings, groups);
        this.penalty = Penalty.calculate(games, serie, teamSettings);
    }

    public getGameColor(groupId: string): string {
        return this._groupColorScheme.getColor(groupId);
    }

    public getOpponentColor(groupId: string): string {
        return this.getGameColor(groupId);
    }

    public get groupLegend(): { name: string; color: string }[] {
        return this._groupColorScheme.legend;
    }

    public get fields(): number {
        return this.venueModels.length;
    }

    public get teamSettings(): TeamSettingEntity[] {
        return this._teamSettings;
    }

    public addRow(): void {
        for (const venue of this.venueModels) {
            const game = new ScheduleRowEntity();
            game.fieldId = venue.fieldId;
            game.homeTeam = ScheduleRowEntity.freeSlotTeamName;
            game.interval = this.intervalCount;
            game.opponentTeam = ScheduleRowEntity.freeSlotTeamName;
            this._scheduleRows.add(game);
        }
        this.intervalCount++;
        this._setup();
    }

    public deleteLastRow(): void {

        const gamesToRemove = this._scheduleRows.where(g => g.interval === (this.intervalCount - 1));
        gamesToRemove.forEach(g => this._scheduleRows.remove(g));
        this.intervalCount--;
        this._setup();
    }
    public addExtraGame(team: TeamSettingEntity): void {
        this._incrementExtraGame(team);
        this._addRemovedTeams();
    }

    private _incrementExtraGame(team: TeamSettingEntity): void {
        let extraGame = this._extraGames.firstOrDefault(e => e.teamSetting === team);
        if (extraGame == null) {
            extraGame = new TeamExtraGame();
            extraGame.teamSetting = team;
            this._extraGames.add(extraGame);
        }
        extraGame.extraGameCount += 1;
    }

    public getEditedScheduleRows(): ScheduleRowEntity[] {
        return this.gameRows.selectMany(gr => gr.games).select(g => g.game).where(g => !g.isEmpty);
    }
    public removeExtraGame(team: TeamSettingEntity): void {
        const extraGame = this._extraGames.firstOrDefault(e => e.teamSetting === team);
        if (extraGame != null) {
            this._extraGames.remove(extraGame);
        }
    }

    public dropRemoveTeam(ev: DragEvent): void {

        ev.preventDefault();
        const sourceTeam = ev.dataTransfer.getData('team');
        const sourceTeamType = parseInt(ev.dataTransfer.getData('type'), 10) as TeamDropInfoType;
        const sourceInterval = parseInt(ev.dataTransfer.getData('interval'), 10);
        const sourceVenueKey = ev.dataTransfer.getData('venueKey');
        const dropData = new TeamDropInfo(sourceTeam, sourceTeamType, sourceInterval, sourceVenueKey, ScheduleViewModel.freeSlotTeamName, TeamDropInfoType.removedTeam, -1, '');
        // console.log('dropdata remove team: ' + dropData.toString());
        this._onDropCallback(dropData);

    }

    public allow(ev: DragEvent): void {
        ev.preventDefault();
    }

    public tapTeam(teamGame: TeamGame, isHome: boolean): void {
        // If a removed team chip was selected, place it in this slot
        if (this.selectedRemovedTeam !== null) {
            const targetTeam = isHome ? teamGame.homeTeam : teamGame.opponentTeam;
            const targetType = isHome ? TeamDropInfoType.homeTeam : TeamDropInfoType.opponentTeam;
            const dropData = new TeamDropInfo(
                this.selectedRemovedTeam.teamName, TeamDropInfoType.removedTeam, -1, '',
                targetTeam, targetType, teamGame.interval, teamGame.venueKey
            );
            this.selectedRemovedTeam = null;
            this._onDropCallback(dropData);
            return;
        }
        // Deselect if same cell tapped again
        if (this.selectedTeamGame === teamGame && this.selectedIsHome === isHome) {
            this.selectedTeamGame = null;
            return;
        }
        // If another cell was already selected, perform a swap
        if (this.selectedTeamGame !== null) {
            const sourceTeam = this.selectedIsHome ? this.selectedTeamGame.homeTeam : this.selectedTeamGame.opponentTeam;
            const targetTeam = isHome ? teamGame.homeTeam : teamGame.opponentTeam;
            const sourceType = this.selectedIsHome ? TeamDropInfoType.homeTeam : TeamDropInfoType.opponentTeam;
            const targetType = isHome ? TeamDropInfoType.homeTeam : TeamDropInfoType.opponentTeam;
            const dropData = new TeamDropInfo(
                sourceTeam, sourceType, this.selectedTeamGame.interval, this.selectedTeamGame.venueKey,
                targetTeam, targetType, teamGame.interval, teamGame.venueKey
            );
            this.selectedTeamGame = null;
            this._onDropCallback(dropData);
            return;
        }
        // Nothing selected yet — select this cell
        this.selectedTeamGame = teamGame;
        this.selectedIsHome = isHome;
    }

    public tapRemovedTeam(removedTeam: RemovedTeam): void {
        // If a schedule slot is selected, place the removed team there
        if (this.selectedTeamGame !== null) {
            const targetTeam = this.selectedIsHome ? this.selectedTeamGame.homeTeam : this.selectedTeamGame.opponentTeam;
            const targetType = this.selectedIsHome ? TeamDropInfoType.homeTeam : TeamDropInfoType.opponentTeam;
            const dropData = new TeamDropInfo(
                removedTeam.teamName, TeamDropInfoType.removedTeam, -1, '',
                targetTeam, targetType, this.selectedTeamGame.interval, this.selectedTeamGame.venueKey
            );
            this.selectedTeamGame = null;
            this._onDropCallback(dropData);
            return;
        }
        // Toggle selection of the removed team chip
        if (this.selectedRemovedTeam === removedTeam) {
            this.selectedRemovedTeam = null;
            return;
        }
        this.selectedRemovedTeam = removedTeam;
    }

    public removeSelectedFromSchedule(): void {
        if (this.selectedTeamGame === null) {
            return;
        }
        const sourceTeam = this.selectedIsHome ? this.selectedTeamGame.homeTeam : this.selectedTeamGame.opponentTeam;
        const sourceType = this.selectedIsHome ? TeamDropInfoType.homeTeam : TeamDropInfoType.opponentTeam;
        const dropData = new TeamDropInfo(
            sourceTeam, sourceType, this.selectedTeamGame.interval, this.selectedTeamGame.venueKey,
            ScheduleViewModel.freeSlotTeamName, TeamDropInfoType.removedTeam, -1, ''
        );
        this.selectedTeamGame = null;
        this._onDropCallback(dropData);
    }

    private _setup(): void {

        this.selectedTeamGame = null;
        this.selectedRemovedTeam = null;
        this.gameRows.clear();
        const customIntervals = this._customIntervals.slice();
        let startTime = this._serie.startTime.toMoment();
        // Custom rows before first game
        const customRowsBeforeFirstGame = customIntervals.where(ci => ci.afterInterval === -1);
        for (const customInterval of customRowsBeforeFirstGame) {
            const customRow = new GameRow(-1, startTime.subtract(customInterval.duration, 'minutes'), customInterval.duration, false);
            customRow.message = customInterval.text;
            startTime.add(customInterval.duration, 'minutes');
            this.gameRows.add(customRow);
            customIntervals.remove(customInterval);
        }

        const matchLengthInMinutes = this._serie.periods * this._serie.periodLength + this._serie.periodPause * (this._serie.periods - 1);
        const intervalCount = this._scheduleRows.orderBy(g => g.interval).lastOrDefault().interval + 1;
        for (let currentInterval = 0; currentInterval < intervalCount; currentInterval++) {
            const rows = this._scheduleRows.where(s => s.interval === currentInterval);

            const gameRow = new GameRow(currentInterval, startTime, matchLengthInMinutes, false);
            this.gameRows.add(gameRow);

            for (const venueModel of this.venueModels) {
                let game: ScheduleRowEntity = null;
                game = rows != null ? rows.firstOrDefault(g => g.fieldId === venueModel.fieldId) : null;

                if (game == null) {
                    game = new ScheduleRowEntity();
                    game.fieldId = venueModel.fieldId;
                    game.interval = currentInterval;
                    game.homeTeam = ScheduleRowEntity.freeSlotTeamName;
                    game.opponentTeam = ScheduleRowEntity.freeSlotTeamName;
                    // this.scheduleRows.add(game);
                }

                startTime = moment(gameRow.endTime);
                const teamGame = new TeamGame(game, this.fields, (dropData: TeamDropInfo) => this._onDropCallback(dropData), this._teamSettings);
                gameRow.addGame(teamGame);
            }
            let addedCustomInterval = false;
            for (const customInterval of customIntervals) {
                if (customInterval.afterInterval === currentInterval) {
                    const customRow = new GameRow(currentInterval, startTime, customInterval.duration, false);
                    customRow.message = customInterval.text;
                    startTime.add(customInterval.duration, 'minutes');
                    this.gameRows.add(customRow);
                    addedCustomInterval = true;
                    customIntervals.remove(customInterval);
                }
            }
            if (!addedCustomInterval) {
                startTime.add(this._serie.gamePause, 'minutes');
            }
        }

        const lastRow = this.gameRows.where(g => !g.isCustomRow).lastOrDefault();
        const customRowsAfterLastGame = customIntervals.where(ci => ci.afterInterval >= lastRow.interval).orderBy(ci => ci.afterInterval);
        startTime = moment(lastRow.endTime);
        let nextInterval = lastRow.interval + 1;
        for (const customInterval of customRowsAfterLastGame) {
            const customRow = new GameRow(nextInterval, startTime, customInterval.duration, false);
            customRow.message = customInterval.text;
            this.gameRows.add(customRow);
            startTime.add(customInterval.duration, 'minutes');
            nextInterval++;
            customIntervals.remove(customInterval);
        }

        this.opponentList.clear();

        for (const game of this._scheduleRows) {
            if (game.homeTeam !== ScheduleRowEntity.freeSlotTeamName) {
                let homeTeamList = this.opponentList.firstOrDefault(ol => ol.team === game.homeTeam && ol.groupId === game.groupId);
                if (!homeTeamList) {
                    homeTeamList = new OpponentsViewModel(game.homeTeam, game.groupId);
                    this.opponentList.add(homeTeamList);
                }
                homeTeamList.addOpponent(game.opponentTeam);
            }
            if (game.opponentTeam !== ScheduleRowEntity.freeSlotTeamName) {
                let opponentTeamList = this.opponentList.firstOrDefault(ol => ol.team === game.opponentTeam && ol.groupId === game.groupId);
                if (!opponentTeamList) {
                    opponentTeamList = new OpponentsViewModel(game.opponentTeam, game.groupId);
                    this.opponentList.add(opponentTeamList);
                }
                opponentTeamList.addOpponent(game.homeTeam);
            }
        }

        this._addRemovedTeams();
    }

    private _addRemovedTeams(): void {
        this.removedTeams.clear();
        if (!this._teamSettings) {
            return;
        }

        for (const team of this._teamSettings) {
            const extraGameSetting = this._extraGames.firstOrDefault(e => e.teamSetting === team);
            const extraGameCount = extraGameSetting != null ? extraGameSetting.extraGameCount : 0;
            const scheduleRows = this.gameRows.selectMany(tg => tg.games).where(s => s.homeTeam === team.name || s.opponentTeam === team.name);
            const gamesLeft = team.getEffectiveGameCount(this._serie.gameCount) + extraGameCount - scheduleRows.length;
            if (gamesLeft > 0) {
                this.removedTeams.add(new RemovedTeam(team.name, gamesLeft, team.groupId, this.getGameColor(team.groupId), (dropDataParam: TeamDropInfo) => this._onDropCallback(dropDataParam)));
            }
            if (gamesLeft < 0) {
                this._incrementExtraGame(team);
            }
        }
    }

    private _onDropCallback(dropData: TeamDropInfo): void {
        // console.log('dropdata: ' + dropData.toString());

        // Handle source team
        let sourceGame: TeamGame = null;
        let targetGame: TeamGame = null;
        if (dropData.sourceTeamType === TeamDropInfoType.homeTeam) {
            sourceGame = this.gameRows.selectMany(gr => gr.games).firstOrDefault(s => dropData.sourceVenueKey === s.venueKey && dropData.sourceInterval === s.interval && dropData.sourceTeam === s.homeTeam);
        } else if (dropData.sourceTeamType === TeamDropInfoType.opponentTeam) {
            sourceGame = this.gameRows.selectMany(gr => gr.games).firstOrDefault(s => dropData.sourceVenueKey === s.venueKey && dropData.sourceInterval === s.interval && dropData.sourceTeam === s.opponentTeam);
        }

        if (dropData.targetTeamType === TeamDropInfoType.removedTeam) {
            if (sourceGame == null) { return; }
            if (dropData.sourceTeamType === TeamDropInfoType.homeTeam) {
                sourceGame.homeTeam = ScheduleRowEntity.freeSlotTeamName;
            } else if (dropData.sourceTeamType === TeamDropInfoType.opponentTeam) {
                sourceGame.opponentTeam = ScheduleRowEntity.freeSlotTeamName;
            }
            this._refreshAfterTeamMove();
            return;
        }

        // Handle target team
        if (dropData.targetTeamType === TeamDropInfoType.homeTeam) {
            const gamesInThisInterval = this.gameRows.selectMany(gr => gr.games).where(s => dropData.targetInterval === s.interval);
            targetGame = gamesInThisInterval.firstOrDefault(s => dropData.targetVenueKey === s.venueKey && dropData.targetTeam === s.homeTeam);
            if (!this._canMoveBetweenGroups(sourceGame, dropData.sourceTeam, targetGame)) { return; }
            this._assignTargetGroup(targetGame, dropData.sourceTeam);
            if (sourceGame === targetGame && dropData.sourceTeamType === TeamDropInfoType.opponentTeam) {
                targetGame.opponentTeam = targetGame.homeTeam;
                targetGame.homeTeam = dropData.sourceTeam;
                this._refreshAfterTeamMove();
                return;
            }
            if (sourceGame != null) {
                this._setTeam(sourceGame, dropData.sourceTeamType, dropData.targetTeam);
            }
            console.log(`Handle target team: ${dropData.sourceTeam}`);
            if (targetGame.fieldId == null) {
                targetGame.fieldId = this._findFirstFreeField(gamesInThisInterval, targetGame);
                console.log(`Updating fieldId to: ${targetGame.fieldId}`);
            } else {
                console.log(`Keeping fieldId : ${targetGame.fieldId}`);
            }
            this._setTeam(targetGame, dropData.targetTeamType, dropData.sourceTeam);

        } else if (dropData.targetTeamType === TeamDropInfoType.opponentTeam) {
            const gamesInThisInterval = this.gameRows.selectMany(gr => gr.games).where(s => dropData.targetInterval === s.interval);
            targetGame = gamesInThisInterval.firstOrDefault(s => dropData.targetVenueKey === s.venueKey && dropData.targetInterval === s.interval && dropData.targetTeam === s.opponentTeam);
            if (!this._canMoveBetweenGroups(sourceGame, dropData.sourceTeam, targetGame)) { return; }
            this._assignTargetGroup(targetGame, dropData.sourceTeam);
            if (sourceGame === targetGame && dropData.sourceTeamType === TeamDropInfoType.homeTeam) {
                targetGame.homeTeam = targetGame.opponentTeam;
                targetGame.opponentTeam = dropData.sourceTeam;
                this._refreshAfterTeamMove();
                return;
            }
            if (sourceGame != null) {
                this._setTeam(sourceGame, dropData.sourceTeamType, dropData.targetTeam);
            }
            console.log(`Handle target team: ${dropData.sourceTeam}`);
            if (targetGame.fieldId == null) {
                targetGame.fieldId = this._findFirstFreeField(gamesInThisInterval, targetGame);
                console.log(`Updating fieldId to: ${targetGame.fieldId}`);
            } else {
                console.log(`Keeping fieldId : ${targetGame.fieldId}`);
            }
            this._setTeam(targetGame, dropData.targetTeamType, dropData.sourceTeam);

        }
        this._refreshAfterTeamMove();
    }

    private _refreshAfterTeamMove(): void {
        this._clearGroupsFromEmptyGames();
        this._scheduleRows.clear();
        this._scheduleRows.addRange(this.getEditedScheduleRows());
        this._addRemovedTeams();
        this.penalty = Penalty.calculate(this._scheduleRows, this._serie, this._teamSettings);
    }

    private _clearGroupsFromEmptyGames(): void {
        for (const game of this.gameRows.selectMany(row => row.games)) {
            if (game.isEmpty) {
                game.game.groupId = null;
            }
        }
    }

    private _setTeam(game: TeamGame, teamType: TeamDropInfoType, team: string): void {
        if (teamType === TeamDropInfoType.homeTeam) {
            game.homeTeam = team;
        } else if (teamType === TeamDropInfoType.opponentTeam) {
            game.opponentTeam = team;
        }
    }

    private _assignTargetGroup(targetGame: TeamGame, teamName: string): void {
        if (targetGame.groupId == null) {
            targetGame.game.groupId = this._teamSettings.find(team => team.name === teamName)?.groupId ?? null;
        }
    }

    private _canMoveBetweenGroups(sourceGame: TeamGame, sourceTeam: string, targetGame: TeamGame): boolean {
        if (targetGame == null) { return false; }
        const sourceGroupId = sourceGame?.groupId
            ?? this._teamSettings.find(team => team.name === sourceTeam)?.groupId
            ?? null;
        const targetGroupId = targetGame.groupId;
        return sourceGroupId == null || targetGroupId == null || sourceGroupId === targetGroupId;
    }

    private _findFirstFreeField(gamesInThisInterval: TeamGame[], targetGame: TeamGame): number {
        if (targetGame.isEmpty && targetGame.fieldId != null) {
            return targetGame.fieldId; // Keep fieldId
        }
        const gamesWithFieldId = gamesInThisInterval.where(g => g.fieldId != null).orderBy(g => g.fieldId);
        for (let i = 0; i < gamesWithFieldId.length; i++) {
            if (gamesWithFieldId[i].fieldId !== i + 1) { // Found unoccupied field, use it
                return i + 1;
            }
        }
        return gamesWithFieldId.length + 1; // Add field to be sure not to use same fieldId as another team if venue is unset.
    }

}
export class TeamExtraGame {
    public teamSetting: TeamSettingEntity = null;
    public extraGameCount: number = 0;
}

export class VenueModel {
    public fieldId: number = null;
    public venue: VenueEntity = null;

    constructor(fieldId: number, venue: VenueEntity) {
        this.fieldId = fieldId;
        this.venue = venue;
    }
    public get hasVenue(): boolean {
        return this.venue != null;
    }
    public get name(): string {
        return this.venue != null ? this.venue.name : `Plan ${this.fieldId}`;
    }
    public get venueId(): string {
        return this.venue != null ? this.venue.id : null;
    }
    public get venueKey(): string {
        if (this.venue == null && this.fieldId == null) {
            throw new Error('Venue and fieldId is null, this should not happen!');
        }
        return this.venue != null ? this.venue.id : this.fieldId.toString();
    }
}
